import React, { useState } from "react";
import { Link } from "react-router-dom";
import "../../assets/gamestatstable.css";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import Loader from "../Utils/Loader";
import { colors, typography } from "../../assets/theme_setup";

const PlayerStatsTable = ({ playerStats, loading }) => {
  const [activeTab, setActiveTab] = useState("basic");
  const [selectedTeam, setSelectedTeam] = useState(null);

  if (loading) {
    return <Loader />;
  }

  if (!playerStats || Object.keys(playerStats).length === 0) {
    return (
      <div className="player-game-stats-error">No player stats available</div>
    );
  }

  // Format function to round to 1 decimal and remove .0 if it's a whole number
  const formatValue = (value) => {
    const rounded = Math.round(value * 10) / 10;
    return rounded % 1 === 0 ? Math.floor(rounded) : rounded;
  };

  const VideoIcon = () => (
    <span className="player-game-stats-video-icon">
      <VideocamRoundedIcon sx={{ color: "#4CAF50", fontSize: "0.9rem" }} />
    </span>
  );

  const StatWithVideo = ({ value, type, player_id }) => (
    <Link
      to={`/game-footage?type=${type}&player_id=${player_id}`}
      style={{ textDecoration: "none", color: "inherit" }}
    >
      {value}
      <VideoIcon />
    </Link>
  );

  // Team selector
  const teamKeys = Object.keys(playerStats);
  if (!selectedTeam && teamKeys.length > 0) {
    setSelectedTeam(teamKeys[0]);
  }

  // Define stat groups
  const statGroups = {
    basic: [
      {
        key: "minutes_played",
        label: "MIN",
        format: (val) => (val ? parseInt(val) : "0"),
      },
      { key: "field_goals_made", label: "FGM" },
      { key: "field_goals_attempted", label: "FGA" },
      {
        key: "fg_pct",
        label: "FG%",
        calculate: (player) =>
          player.field_goals_attempted
            ? formatValue(
                (player.field_goals_made / player.field_goals_attempted) * 100
              )
            : 0,
      },
      { key: "three_pointers_made", label: "3PM" },
      { key: "three_pointers_attempted", label: "3PA" },
      {
        key: "three_pt_pct",
        label: "3P%",
        calculate: (player) =>
          player.three_pointers_attempted
            ? formatValue(
                (player.three_pointers_made / player.three_pointers_attempted) *
                  100
              )
            : 0,
      },
      { key: "free_throws_made", label: "FTM" },
      { key: "free_throws_attempted", label: "FTA" },
      {
        key: "ft_pct",
        label: "FT%",
        calculate: (player) =>
          player.free_throws_attempted
            ? formatValue(
                (player.free_throws_made / player.free_throws_attempted) * 100
              )
            : 0,
      },
      { key: "points", label: "PTS" },
    ],
    rebounds: [
      { key: "offensive_rebounds", label: "OREB" },
      { key: "defensive_rebounds", label: "DREB" },
      { key: "total_rebounds", label: "REB" },
    ],
    other: [
      { key: "assists", label: "AST" },
      { key: "steals", label: "STL" },
      { key: "blocks", label: "BLK" },
      { key: "turnovers", label: "TO" },
      { key: "personal_fouls", label: "PF" },
    ],
    advanced: [
      {
        key: "xfactor",
        label: "Game XFactor",
        isAdvanced: true,
      },
      {
        key: "true_shooting_percentage",
        label: "TS%",
        format: (val) => (val ? `${val}%` : "0%"),
        isAdvanced: true,
      },
      {
        key: "effective_fg_percentage",
        label: "EFG%",
        format: (val) => (val ? `${val}%` : "0%"),
        isAdvanced: true,
      },
      {
        key: "points_per_possession",
        label: "PPP",
        format: (val) => val || "0",
        isAdvanced: true,
      },
      {
        key: "offensive_rebound_percentage",
        label: "ORB%",
        format: (val) => (val ? `${val}%` : "0%"),
        isAdvanced: true,
      },
      {
        key: "defensive_rebound_percentage",
        label: "DREB%",
        format: (val) => (val ? `${val}%` : "0%"),
        isAdvanced: true,
      },
    ],
  };

  // Get all stats based on active tab
  const getActiveStats = () => {
    switch (activeTab) {
      case "advanced":
        return statGroups.advanced;
      case "all":
        return [
          ...statGroups.basic,
          ...statGroups.rebounds,
          ...statGroups.other,
          ...statGroups.advanced,
        ];
      default:
        return [
          ...statGroups.basic,
          ...statGroups.rebounds,
          ...statGroups.other,
        ];
    }
  };

  const activeStats = getActiveStats();

  return (
    <div className="game-stats-main-container">
      <div className="game-stats-controls">
        <div className="game-stats-team-selector">
          {Object.values(playerStats).map((team, index) => (
            <button
              key={index}
              className={`game-stats-team-btn ${
                selectedTeam === Object.keys(playerStats)[index] ? "active" : ""
              }`}
              onClick={() => setSelectedTeam(Object.keys(playerStats)[index])}
            >
              {team.teamName}
            </button>
          ))}
        </div>

        <div className="game-stats-view-options">
          <button
            className={`game-stats-view-btn ${
              activeTab === "basic" ? "active" : ""
            }`}
            onClick={() => setActiveTab("basic")}
          >
            Basic Stats
          </button>
          <button
            className={`game-stats-view-btn ${
              activeTab === "advanced" ? "active" : ""
            }`}
            onClick={() => setActiveTab("advanced")}
          >
            Advanced Stats
          </button>
          <button
            className={`game-stats-view-btn ${
              activeTab === "all" ? "active" : ""
            }`}
            onClick={() => setActiveTab("all")}
          >
            All Stats
          </button>
        </div>
      </div>

      {selectedTeam && (
        <div className="game-stats-table-container">
          <table className="game-stats-table">
            <thead>
              <tr>
                <th className="game-stats-player-column">Player</th>
                {activeStats.map((stat, index) => (
                  <th
                    key={index}
                    className={
                      stat.isAdvanced ? "game-stats-advanced-column" : ""
                    }
                  >
                    {stat.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {playerStats[selectedTeam].players &&
                playerStats[selectedTeam].players.map((player, playerIndex) => (
                  <tr key={playerIndex} className="game-stats-player-row">
                    <td className="game-stats-player-info">
                      <img
                        src={player.image_url || "/default-player.png"}
                        alt={player.player_name}
                        className="game-stats-player-image"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/default-player.png";
                        }}
                      />
                      <div className="game-stats-player-name-container">
                        <span className="game-stats-player-name">
                          {player.player_name}
                        </span>
                      </div>
                    </td>
                    {activeStats.map((stat, statIndex) => {
                      let value;
                      if (stat.calculate) {
                        value = stat.calculate(player);
                      } else if (stat.format) {
                        value = stat.format(player[stat.key]);
                      } else {
                        value = player[stat.key] || 0;
                      }

                      return (
                        <td
                          key={statIndex}
                          className={
                            stat.isAdvanced ? "game-stats-advanced-column" : ""
                          }
                        >
                          {value}
                        </td>
                      );
                    })}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="game-stats-legend">
        <div className="game-stats-legend-item">
          <span className="game-stats-legend-color advanced"></span>
          <span className="game-stats-legend-text">Advanced Stats</span>
        </div>
      </div>
    </div>
  );
};

export default PlayerStatsTable;
